import { useState } from 'react'
import { attachmentContentUrl } from '../../api/endpoints'
import type { Attachment, Message } from '../../api/types'
import { Icon } from '../../components/Icon'
import { isImageType } from '../../lib/files'
import { fileTypeLabel, formatFileSize } from '../../lib/format'
import { AudioAttachment } from '../media/AudioAttachment'
import { VideoAttachment } from '../media/VideoAttachment'
import { BlurhashCanvas } from './BlurhashCanvas'
import { useConversation } from './ConversationContext'

const MAX_IMAGE_HEIGHT = 220

function frameHeight(a: Attachment, width: number): number {
  if (a.width && a.height) return Math.min(MAX_IMAGE_HEIGHT, Math.max(60, Math.round((width * a.height) / a.width)))
  return 160
}

function ImageAttachment({ attachment, pending }: { attachment: Attachment; pending: boolean }) {
  const { openAttachment } = useConversation()
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  // Thumbnails are cached by the service worker; before the worker made one, fall back to the original.
  const src = pending ? null : attachmentContentUrl(attachment.id, attachment.hasThumb ? 'thumb' : 'original')
  const loaded = src !== null && loadedSrc === src
  const failed = src !== null && failedSrc === src
  const showPlaceholder = failed || (!src && !attachment.blurhash)

  return (
    <button
      type="button"
      className="image-card"
      aria-label={`Open image: ${attachment.fileName}`}
      onClick={() => !pending && openAttachment(attachment)}
      disabled={pending}
    >
      {showPlaceholder ? (
        <div className="image-placeholder">
          <Icon name="image" />
          <span>{pending ? 'Sending…' : failed ? 'Image unavailable' : attachment.fileName}</span>
        </div>
      ) : (
        <div className="image-frame" style={{ height: frameHeight(attachment, 254) }}>
          {attachment.blurhash && !loaded ? <BlurhashCanvas hash={attachment.blurhash} /> : null}
          {src && !failed ? (
            <img
              key={src}
              src={src}
              alt={attachment.fileName}
              loading="lazy"
              decoding="async"
              className={loaded ? 'loaded' : undefined}
              onLoad={() => setLoadedSrc(src)}
              onError={() => setFailedSrc(src)}
            />
          ) : null}
        </div>
      )}
    </button>
  )
}

function FileAttachment({ attachment, pending }: { attachment: Attachment; pending: boolean }) {
  const type = fileTypeLabel(attachment.fileName)
  const content = (
    <>
      <span className="file-symbol" data-file-type={type}>
        {type}
      </span>
      <span className="file-info">
        <strong>{attachment.fileName}</strong>
        <small>
          {formatFileSize(attachment.sizeBytes)} <span aria-hidden="true">·</span> {type} {pending ? '· Sending…' : 'document'}
        </small>
      </span>
      {!pending ? <span className="document-open">Open</span> : null}
    </>
  )
  if (pending) return <div className="document-card">{content}</div>
  return (
    <a
      className="document-card"
      href={attachmentContentUrl(attachment.id, 'original')}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${attachment.fileName} in a new tab`}
    >
      {content}
    </a>
  )
}

export function AttachmentList({ message, pending }: { message: Message; pending: boolean }) {
  const { me } = useConversation()
  const mine = message.authorId === me.id
  return (
    <div className="attachments">
      {message.attachments.map((a) =>
        isImageType(a.contentType) ? (
          <ImageAttachment key={a.id} attachment={a} pending={pending} />
        ) : a.kind === 'video' ? (
          <VideoAttachment key={a.id} attachment={a} pending={pending} mine={mine} />
        ) : a.kind === 'audio' ? (
          <AudioAttachment key={a.id} attachment={a} pending={pending} mine={mine} />
        ) : (
          <FileAttachment key={a.id} attachment={a} pending={pending} />
        ),
      )}
    </div>
  )
}
